import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import './langSelector.css'

interface LanguageSelectorProps {
  selectedLanguage: string
  onSelect: (code: string) => void
}

const LANGUAGES = [
  { code: 'en', short: 'EN', labelKey: 'english', fallback: 'English' },
  { code: 'fr', short: 'FR', labelKey: 'french', fallback: 'Français' },
] as const

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  selectedLanguage,
  onSelect,
}) => {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const activeCode = (selectedLanguage || i18n.language || 'en')
    .slice(0, 2)
    .toLowerCase()
  const active =
    LANGUAGES.find((l) => l.code === activeCode) || LANGUAGES[0]

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const pick = (code: string) => {
    if (code !== activeCode) {
      onSelect(code)
      i18n.changeLanguage(code)
      localStorage.setItem('i18nextLng', code)
    }
    setOpen(false)
  }

  return (
    <div className="incyia-lang" ref={rootRef}>
      <button
        type="button"
        className={`incyia-lang-trigger${open ? ' is-open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language', { defaultValue: 'Language' })}
        onClick={() => setOpen((v) => !v)}
      >
        <svg
          className="incyia-lang-globe"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          width="15"
          height="15"
          aria-hidden
        >
          <circle cx="8" cy="8" r="6.2" />
          <path d="M2 8h12M8 2c1.6 1.7 2.4 3.7 2.4 6S9.6 12.3 8 14c-1.6-1.7-2.4-3.7-2.4-6S6.4 3.7 8 2z" />
        </svg>
        <span className="incyia-lang-code">{active.short}</span>
        <KeyboardArrowDownRoundedIcon
          className="incyia-lang-caret"
          sx={{ fontSize: 16 }}
        />
      </button>

      {open ? (
        <div className="incyia-lang-menu" role="listbox" aria-label="Language">
          <div className="incyia-lang-menu-label">
            {t('language', { defaultValue: 'Language' })}
          </div>
          {LANGUAGES.map((lang) => {
            const selected = lang.code === activeCode
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={selected}
                className={`incyia-lang-option${selected ? ' is-active' : ''}`}
                onClick={() => pick(lang.code)}
              >
                <span className="incyia-lang-option-short">{lang.short}</span>
                <span className="incyia-lang-option-name">
                  {t(lang.labelKey, { defaultValue: lang.fallback })}
                </span>
                {selected ? (
                  <CheckRoundedIcon
                    className="incyia-lang-check"
                    sx={{ fontSize: 16 }}
                  />
                ) : (
                  <span className="incyia-lang-check-slot" aria-hidden />
                )}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export default LanguageSelector
